import React from "react";
import { Download, X, CaretLeft, CaretRight, FileText, FilePdf, FileImage, FileDoc, FileXls, FileZip, File as FileGeneric, ShareNetwork } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { shareFile } from "@/lib/shareFile";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Iter151: worker pdfjs servito da unpkg (CDN) — evita di dover configurare
// il bundler webpack per un asset .worker.js. Il worker viene lazy-loaded al
// primo PDF renderizzato.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp"];
const isImage = (name = "") => IMAGE_EXTS.includes((name.split(".").pop() || "").toLowerCase());
const isPdf = (name = "") => (name.split(".").pop() || "").toLowerCase() === "pdf";

function DocIcon({ filename, size = 22 }) {
  const ext = (filename?.split(".").pop() || "").toLowerCase();
  const common = { size, weight: "duotone" };
  if (["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp"].includes(ext)) return <FileImage {...common} color="#0EA5E9" />;
  if (ext === "pdf") return <FilePdf {...common} color="#DC2626" />;
  if (["doc", "docx"].includes(ext)) return <FileDoc {...common} color="#2563EB" />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileXls {...common} color="#059669" />;
  if (["zip", "rar", "7z"].includes(ext)) return <FileZip {...common} color="#7C3AED" />;
  if (["txt", "md"].includes(ext)) return <FileText {...common} color="#6D28D9" />;
  return <FileGeneric {...common} color="#64748B" />;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Modal riusabile per anteprima inline di file operativi/immobile.
 *
 * Modalità A — Single doc (compatibilità con l'API precedente):
 *   <FilePreviewModal doc={doc} onClose={fn} />
 *
 * Modalità B — Galleria con navigazione ← / →:
 *   <FilePreviewModal docs={[...]} index={i} onIndexChange={setI} onClose={fn} />
 *
 * Le frecce (bottoni + tasti ArrowLeft/ArrowRight) permettono di navigare
 * tra i documenti senza chiudere il modal. Wrap-around su lista.
 */
export function FilePreviewModal({ doc, docs, index, onIndexChange, onClose }) {
  const list = Array.isArray(docs) ? docs : (doc ? [doc] : []);
  // Modalità gallery: il modale è aperto SOLO se `index` è un numero valido.
  // Modalità single-doc: aperto se `doc` è presente.
  // Il default `activeIdx=0` precedente causava un bug in prod: alla chiusura
  // (index=null) `current` ricadeva su list[0] e `open=!!current` restava true.
  const isGallery = Array.isArray(docs);
  const hasValidIndex = typeof index === "number" && index >= 0 && index < list.length;
  const activeIdx = hasValidIndex ? index : 0;
  const current = isGallery ? (hasValidIndex ? list[activeIdx] : null) : (doc || null);
  const open = !!current;
  const canNav = isGallery && list.length > 1 && typeof onIndexChange === "function";

  const go = React.useCallback((delta) => {
    if (!canNav) return;
    const next = (activeIdx + delta + list.length) % list.length;
    onIndexChange(next);
  }, [canNav, activeIdx, list.length, onIndexChange]);

  React.useEffect(() => {
    if (!open || !canNav) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(+1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canNav, go]);

  if (!current) return null;
  // Preferisci l'URL firmato (con TTL) restituito dal backend; fallback a
  // file_url per retrocompatibilità con dati vecchi non arricchiti.
  const raw = current.signed_url || current.file_url;
  const src = raw && raw.startsWith("/") ? `${API_ORIGIN}${raw}` : raw;
  const isImg = isImage(current.filename) || (current.mime_type || "").startsWith("image/");
  const isPdfFile = isPdf(current.filename) || (current.mime_type || "") === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col [&>button.absolute]:hidden"
        data-testid="file-preview-modal"
      >
        <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <DocIcon filename={current.filename} size={22} />
            <div className="min-w-0">
              <DialogTitle className="text-sm font-medium truncate">{current.filename}</DialogTitle>
              <div className="text-xs text-muted-foreground">
                {fmtSize(current.file_size)}
                {canNav && <span className="ml-2 font-mono">{activeIdx + 1} / {list.length}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Iter140l: condivisione nativa mobile (WhatsApp/AirDrop) con
                fallback URL + clipboard. Signed URL rigenerato al momento. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => shareFile({ url: src, filename: current.filename, title: current.filename })}
              data-testid="preview-share-btn"
            >
              <ShareNetwork size={14} className="mr-2" /> Condividi
            </Button>
            <a href={src} target="_blank" rel="noopener noreferrer" download={current.filename}>
              <Button variant="outline" size="sm" data-testid="preview-download-btn">
                <Download size={14} className="mr-2" /> Scarica
              </Button>
            </a>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose && onClose(); }}
              className="h-11 w-11 rounded-full"
              data-testid="preview-close-btn"
              aria-label="Chiudi anteprima"
            >
              <X size={20} weight="bold" />
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-auto flex items-center justify-center p-2 relative">
          {/* Iter207.13 — Bottone di chiusura flottante SEMPRE visibile su
              mobile, anche sopra iframe PDF su iOS Safari. Tap area 44x44,
              cerchio bianco semi-opaco con X scura per contrasto su qualsiasi
              sfondo. Nascosto su desktop dove basta l'X in header. */}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose && onClose(); }}
            className="md:hidden absolute top-2 right-2 z-50 w-11 h-11 rounded-full bg-white/95 hover:bg-white shadow-lg border border-slate-300 flex items-center justify-center text-slate-800 active:bg-slate-100 backdrop-blur-sm"
            style={{ WebkitTapHighlightColor: "transparent" }}
            aria-label="Chiudi anteprima"
            data-testid="preview-close-floating"
          >
            <X size={22} weight="bold" />
          </button>
          {isImg && (
            <img
              src={src}
              alt={current.filename}
              className="max-w-full max-h-full object-contain"
              data-testid="preview-img"
            />
          )}
          {isPdfFile && (
            <PdfViewer src={src} fileId={current._id} filename={current.filename} />
          )}
          {!isImg && !isPdfFile && (
            <div className="text-center text-muted-foreground p-8">
              Anteprima non disponibile per questo formato.<br/>
              <a href={src} target="_blank" rel="noopener noreferrer" className="text-purple-700 underline mt-2 inline-block">
                Aprilo in una nuova scheda
              </a>
            </div>
          )}
          {canNav && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white shadow-lg border flex items-center justify-center text-slate-700 hover:text-purple-700 transition"
                aria-label="Precedente"
                data-testid="preview-prev-btn"
              >
                <CaretLeft size={22} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => go(+1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white shadow-lg border flex items-center justify-center text-slate-700 hover:text-purple-700 transition"
                aria-label="Successivo"
                data-testid="preview-next-btn"
              >
                <CaretRight size={22} weight="bold" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { isImage, isPdf, DocIcon, fmtSize };


/**
 * Iter151 — Viewer PDF basato su react-pdf/pdf.js che disegna il PDF pagina
 * per pagina come <canvas>, così è visibile anche su browser mobile che non
 * supportano il rendering nativo di PDF dentro un <iframe>.
 *
 * UX:
 *   • Mostra la pagina corrente centrata, larga ~90% del container.
 *   • Se il PDF ha >1 pagina, mostra 2 tasti "‹" e "›" per navigare.
 *   • Sotto: paginatore "1 / N".
 *   • In caso di errore fetching/parsing → link fallback "Apri in nuova scheda".
 */
function PdfViewer({ src, fileId, filename }) {
  const [numPages, setNumPages] = React.useState(0);
  const [pageIdx, setPageIdx] = React.useState(1);
  const [errored, setErrored] = React.useState(false);
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(700);

  // Adatta la larghezza della pagina al container (responsive mobile).
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      setWidth(Math.max(240, Math.min(w - 32, 1200)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset alla prima pagina se cambia il file.
  React.useEffect(() => {
    setPageIdx(1);
    setNumPages(0);
    setErrored(false);
  }, [fileId]);

  const onLoad = ({ numPages: n }) => {
    setNumPages(n);
    setErrored(false);
  };

  const goPage = (delta) => {
    setPageIdx((p) => Math.min(Math.max(1, p + delta), numPages || 1));
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex flex-col items-center justify-start overflow-auto bg-slate-200 py-4"
      data-testid="preview-pdf"
    >
      {errored ? (
        <div className="text-center text-muted-foreground p-8">
          Anteprima non disponibile.
          <br />
          <a href={src} target="_blank" rel="noopener noreferrer"
             className="text-purple-700 underline mt-2 inline-block"
             data-testid="preview-pdf-fallback">
            Aprilo in una nuova scheda →
          </a>
        </div>
      ) : (
        <Document
          file={src}
          onLoadSuccess={onLoad}
          onLoadError={() => setErrored(true)}
          onSourceError={() => setErrored(true)}
          loading={
            <div className="text-sm text-muted-foreground py-10">
              Caricamento anteprima PDF…
            </div>
          }
        >
          <Page
            pageNumber={pageIdx}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-lg bg-white"
          />
        </Document>
      )}
      {numPages > 1 && !errored && (
        <div className="mt-3 flex items-center gap-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full border shadow-sm">
          <button
            type="button"
            onClick={() => goPage(-1)}
            disabled={pageIdx <= 1}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Pagina precedente"
            data-testid="pdf-page-prev"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <div className="text-xs font-mono font-semibold text-slate-700 tabular-nums" data-testid="pdf-page-indicator">
            {pageIdx} / {numPages}
          </div>
          <button
            type="button"
            onClick={() => goPage(+1)}
            disabled={pageIdx >= numPages}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Pagina successiva"
            data-testid="pdf-page-next"
          >
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

